import ReportsSettingsPanel, { ConfirmationModal } from './modules/reports/components/ReportsSettingsView';
import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from './firebase-config';
import {
  getActiveCompanyId,
  DEFAULT_COMPANY_ID,
  DEFAULT_COMPANY_NAME
} from './core/company/companyConfig';
import { resolveServicesCollectionPath } from './core/company/companyPaths';
import {
  calculateRouteCost,
  normalizeLiquidationSettings,
  DEFAULT_LIQUIDATION_SETTINGS
} from './modules/logistics/components/LogisticsUtils';
import HistoryView from './modules/reports/components/HistoryView';
import BalanceGeneralView from './modules/reports/components/BalanceGeneralView';
import LiveMonitorView from './modules/logistics/components/LiveMonitorView';
import { HistoryDetailModal, AnnualExpenseDetailView, YearlyHistoryModal } from './modules/reports/components/ReportDetailModals';
import { DetailedTransporteView, DetailedFlotaView, DetailedKmsView } from './modules/reports/components/BalanceDetailViews';
import {
  OFFICE_LOCATION,
  formatCurrency,
  getOfficeLocationFromSettings,
  getRouteTotalDistance,
  sortRoutesDesc
} from './modules/reports/utils/routeMonitorUtils';
import useRouteMonitorData from './modules/reports/hooks/useRouteMonitorData';

const CustomStyles = () => (
  <style>{`
    @keyframes shimmer {
      0% {
        transform: translateX(-100%) skewX(-20deg);
        opacity: 0;
      }
      20% {
        transform: translateX(100%) skewX(-20deg);
        opacity: 0.5;
      }
      100% {
        transform: translateX(200%) skewX(-20deg);
        opacity: 0;
      }
    }
    .shimmer::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 50%;
      height: 100%;
      background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0) 100%);
      animation: shimmer 3s infinite linear;
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in {
      animation: fadeIn 0.5s ease-out forwards;
    }
    .animate-fade-in-down {
      animation: fadeIn 0.5s ease-out forwards;
    }
    @keyframes pulse-live {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    .animate-pulse-live {
      animation: pulse-live 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }
  `}</style>
);

const Icon = ({ path, className = 'w-6 h-6' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const SectionPill = ({ iconPath, text, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    violet: 'border-violet-200 bg-violet-50 text-violet-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700'
  };

  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${tones[tone] || tones.slate}`}>
      {iconPath ? <Icon path={iconPath} className="h-4 w-4" /> : null}
      <span>{text}</span>
    </span>
  );
};

const RouteMonitorTabs = ({ activeTab, setActiveTab, availableTabs, showTabBar }) => {
  if (!showTabBar) return null;

  const tabs = [
    { key: 'live', label: 'En vivo', icon: 'M12 18v.01M12 6v.01M6 12h.01M18 12h.01M16.243 7.757l.01.01M7.757 16.243l.01.01M16.243 16.243l.01-.01M7.757 7.757l.01-.01' },
    { key: 'history', label: 'Historial', icon: 'M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z' },
    { key: 'balance', label: 'Balance general', icon: 'M3.75 3v18h16.5M7.5 14.25l3-3 2.25 2.25 4.5-4.5' },
    { key: 'settings', label: 'Configuración', icon: 'M10.5 6h3m-7.5 6h11.25m-9 6h6.75' }
  ].filter((tab) => availableTabs.includes(tab.key));

  return (
    <div id="route-monitor-tabs" className="mb-6 overflow-x-auto">
      <div className="inline-flex min-w-full gap-2 rounded-[24px] border border-slate-200 bg-white p-2 shadow-sm">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`inline-flex flex-1 items-center justify-center gap-2 rounded-[18px] px-4 py-2.5 text-sm font-bold transition ${
                isActive
                  ? 'bg-[linear-gradient(180deg,#1e293b,#0f172a)] text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)]'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon path={tab.icon} className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const RouteMonitorHeader = ({ mode, activeCompanyName, activeTab, availableTabs, onBackToMenu, onStartTutorial, tutorialSeen = false }) => {
  const isReportsMode = mode === 'reports';
  const headerTitle = isReportsMode ? 'Reportes y liquidación' : 'Monitor en vivo';
  const headerDescription = isReportsMode
    ? 'Revisá historial, balance y reglas de liquidación desde una sola vista.'
    : 'Seguimiento vivo de rutas, unidades y actividad operativa en tiempo real.';

  const tabLabelMap = {
    live: 'Vista en vivo',
    history: 'Historial',
    balance: 'Balance general',
    settings: 'Liquidación y reglas'
  };

  return (
    <section id="monitor-global-header" className="relative mb-6 overflow-hidden rounded-[30px] border border-slate-200 bg-white p-5 shadow-sm md:p-6">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.10),transparent_30%),radial-gradient(circle_at_bottom_left,rgba(99,102,241,0.08),transparent_24%)]" />
      <div className="relative flex flex-col gap-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onBackToMenu}
              className="inline-flex min-w-[10.5rem] items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-[linear-gradient(180deg,#1e293b,#0f172a)] px-4 py-2 text-sm font-bold text-white shadow-[0_16px_30px_rgba(15,23,42,0.18)] transition hover:-translate-y-[1px] hover:bg-slate-800"
            >
              <Icon path="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" className="h-4 w-4" />
              <span>Volver</span>
            </button>
            <span className="text-slate-300">|</span>
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500">Centro operativo</p>
            <h1 className="text-xl font-bold tracking-[-0.03em] text-slate-900 md:text-2xl">
              {headerTitle}
            </h1>
            {!isReportsMode ? (
              <>
                <span className="text-slate-300">|</span>
                <p className="text-sm font-medium text-slate-500">
                  seguimiento de rutas, unidades y actividad en tiempo real
                </p>
              </>
            ) : null}
            {onStartTutorial ? (
              <button
                id="monitor-global-tutorial"
                type="button"
                onClick={onStartTutorial}
                className={`inline-flex items-center gap-2 rounded-2xl border border-cyan-300 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-800 transition-all hover:-translate-y-0.5 hover:bg-cyan-100 ${tutorialSeen ? '' : 'animate-pulse shadow-[0_0_0_6px_rgba(34,211,238,0.18),0_0_24px_rgba(34,211,238,0.32)]'}`}
              >
                <Icon path="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" className="h-4 w-4" />
                Tutorial guiado
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm xl:justify-end">
            <SectionPill tone="slate" text={activeCompanyName} />
            <SectionPill tone={isReportsMode ? 'violet' : 'blue'} text={tabLabelMap[activeTab] || 'Panel activo'} />
            <SectionPill tone="emerald" text={`${availableTabs.length} vistas habilitadas`} />
          </div>
        </div>
      </div>
    </section>
  );
};

const RouteMonitor = ({
  onBackToMenu,
  showMessage,
  companyId: companyIdProp,
  companyName: companyNameProp,
  mode = 'monitor',
  allowedTabs,
  onOpenRouteToLogistics,
  onStartTutorial,
  tutorialSeen = false,
  tutorialActive = false,
  tutorialFocusTarget = null,
  onMonitorTutorialContextChange,
  onGiaContextChange,
  onGoToLogistics
}) => {
  const activeCompanyId = companyIdProp || getActiveCompanyId() || DEFAULT_COMPANY_ID;
  const activeCompanyName = companyNameProp || DEFAULT_COMPANY_NAME;

  const availableTabs = useMemo(() => {
    if (Array.isArray(allowedTabs) && allowedTabs.length > 0) {
      return allowedTabs;
    }

    return mode === 'reports' ? ['history', 'balance', 'settings'] : ['live'];
  }, [allowedTabs, mode]);

  const initialTab = availableTabs[0] || 'live';
  const showTabBar = availableTabs.length > 1;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [allServices, setAllServices] = useState([]);

  const {
    loading,
    error,
    showHistoryDetail,
    selectedHistoryRoute,
    allRoutes,
    allFleetExpenses,
    fleetCategories,
    settings,
    isConfirmModalOpen,
    showYearlyHistory,
    activeRoutes,
    setShowHistoryDetail,
    setIsConfirmModalOpen,
    setShowYearlyHistory,
    handleSaveSettings,
    requestResetData,
    handleConfirmResetData,
    handleShowHistoryDetail,
    handleSelectRouteFromLiveMonitor,
    stockReportData
  } = useRouteMonitorData({
    activeCompanyId,
    activeCompanyName,
    showMessage
  });

  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(initialTab);
    }
  }, [activeTab, availableTabs, initialTab]);

  useEffect(() => {
    if (typeof onMonitorTutorialContextChange === 'function') {
      onMonitorTutorialContextChange({
        hasActiveRoutes: Array.isArray(activeRoutes) && activeRoutes.length > 0,
      });
    }
  }, [activeRoutes, onMonitorTutorialContextChange]);

  useEffect(() => {
    const servicesRef = collection(db, resolveServicesCollectionPath(activeCompanyId));
    const servicesQuery = query(servicesRef);

    const unsubscribe = onSnapshot(servicesQuery, (snapshot) => {
      const nextServices = snapshot.docs.map((serviceDoc) => ({
        id: serviceDoc.id,
        ...serviceDoc.data()
      }));
      setAllServices(nextServices);
    });

    return () => unsubscribe();
  }, [activeCompanyId]);

  useEffect(() => {
    if (typeof onGiaContextChange !== 'function') return;

    const tabMap = {
      live: 'Monitor en tiempo real',
      history: 'Historial operativo',
      balance: 'Balance general',
        settings: 'Liquidación y reglas',
    };

    const subsectionLabel = tabMap[activeTab] || (mode === 'reports' ? 'Historial operativo' : 'Monitor en tiempo real');
    const focusLabel =
      showHistoryDetail && selectedHistoryRoute
        ? selectedHistoryRoute.name || selectedHistoryRoute.routeName || selectedHistoryRoute.id || ''
        : '';

    onGiaContextChange({ subsectionLabel, focusLabel });
  }, [activeTab, mode, onGiaContextChange, selectedHistoryRoute, showHistoryDetail]);

  return (
    <div className="h-full min-h-0 overflow-hidden bg-slate-100 p-4 md:p-6">
      <CustomStyles />

      <div className="flex h-full min-h-0 flex-col gap-4">
        <RouteMonitorHeader
          mode={mode}
          activeCompanyName={activeCompanyName}
          activeTab={activeTab}
          availableTabs={availableTabs}
          onBackToMenu={onBackToMenu}
          onStartTutorial={onStartTutorial}
          tutorialSeen={tutorialSeen}
        />

        <RouteMonitorTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          availableTabs={availableTabs}
          showTabBar={showTabBar}
        />

        <section id="route-monitor-content" className="min-h-0 flex-1 overflow-hidden rounded-[30px] border border-slate-200 bg-white p-4 shadow-sm md:p-5">
          {availableTabs.includes('live') && (
            <div className={activeTab === 'live' ? 'h-full min-h-0' : 'hidden'}>
              <LiveMonitorView
                activeRoutes={activeRoutes}
                loading={loading}
                error={error}
                onSelectRoute={handleSelectRouteFromLiveMonitor}
                onOpenRoute={onOpenRouteToLogistics}
                onGoToLogistics={onGoToLogistics}
                officeLocation={settings.officeLocation || OFFICE_LOCATION}
                tutorialActive={tutorialActive}
                tutorialFocusTarget={tutorialFocusTarget}
              />
            </div>
          )}

          {availableTabs.includes('history') && (
            <div className={activeTab === 'history' ? 'block' : 'hidden'}>
              <HistoryView
                routes={allRoutes}
                services={allServices}
                loading={loading}
                onShowDetail={handleShowHistoryDetail}
                onOpenRoute={onOpenRouteToLogistics}
                settings={settings}
                Icon={Icon}
                sortRoutesDesc={sortRoutesDesc}
                getRouteTotalDistance={getRouteTotalDistance}
              />
            </div>
          )}

          {availableTabs.includes('balance') && (
            <div className={activeTab === 'balance' ? 'block' : 'hidden'}>
              <BalanceGeneralView
                allRoutes={allRoutes}
                allServices={allServices}
                allFleetExpenses={allFleetExpenses}
                stockReportData={stockReportData}
                settings={settings}
                fleetCategories={fleetCategories}
                onShowHistory={() => setShowYearlyHistory(true)}
                calculateRouteCost={calculateRouteCost}
                getOfficeLocationFromSettings={getOfficeLocationFromSettings}
                getRouteTotalDistance={getRouteTotalDistance}
                formatCurrency={formatCurrency}
                AnnualExpenseDetailView={AnnualExpenseDetailView}
                DetailedTransporteView={DetailedTransporteView}
                DetailedFlotaView={DetailedFlotaView}
                DetailedKmsView={DetailedKmsView}
              />
            </div>
          )}

          {availableTabs.includes('settings') && (
            <div className={activeTab === 'settings' ? 'block' : 'hidden'}>
              <ReportsSettingsPanel
                settings={settings}
                onSave={handleSaveSettings}
                onResetRequest={requestResetData}
                showMessage={showMessage}
                formatCurrency={formatCurrency}
                normalizeLiquidationSettings={normalizeLiquidationSettings}
                DEFAULT_LIQUIDATION_SETTINGS={DEFAULT_LIQUIDATION_SETTINGS}
              />
            </div>
          )}
        </section>
      </div>

      <ConfirmationModal
        show={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmResetData}
        title="Confirmar eliminación de datos"
      >
        <p>
          ¿Estás seguro de que quieres eliminar <strong>todas</strong> las rutas del historial?
          Esta acción no se puede deshacer.
        </p>
      </ConfirmationModal>

      <YearlyHistoryModal
        show={showYearlyHistory}
        onClose={() => setShowYearlyHistory(false)}
        allRoutes={allRoutes}
        allFleetExpenses={allFleetExpenses}
        settings={settings}
      />

      <HistoryDetailModal
        show={showHistoryDetail}
        onClose={() => setShowHistoryDetail(false)}
        route={selectedHistoryRoute}
        settings={settings}
        onOpenRoute={onOpenRouteToLogistics}
      />
    </div>
  );
};

export default RouteMonitor;
